import { combineEpics, ofType } from 'redux-observable'
import { EMPTY, of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import {
  cloneGroupSettingsError,
  cloneGroupSettingsSuccess,
  deleteGroupSettingsError,
  deleteGroupSettingsSuccess,
  fetchGroupSettingsError,
  fetchGroupSettingsSuccess,
  setGroupSettingsAsDefaultError,
  setGroupSettingsAsDefaultSuccess,
} from './actions'
import {
  CLONE_GROUP_SETTINGS_REQUEST_ASYNC,
  DELETE_GROUP_SETTINGS_REQUEST_ASYNC,
  FILTERS_SUBMIT,
  GROUP_SETTINGS_DATA_REQUEST_ASYNC,
  PAGINATE_CHANGE,
  SEARCH_CHANGE,
  SET_GROUP_SETTINGS_AS_DEFAULT_REQUEST_ASYNC,
  SORT_PROPERTY_CHANGE,
} from './constants'

const fetchGroupSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GROUP_SETTINGS_DATA_REQUEST_ASYNC.INITIATE,
      CLONE_GROUP_SETTINGS_REQUEST_ASYNC.SUCCESS,
      DELETE_GROUP_SETTINGS_REQUEST_ASYNC.SUCCESS,
      SET_GROUP_SETTINGS_AS_DEFAULT_REQUEST_ASYNC.SUCCESS,
      SORT_PROPERTY_CHANGE,
      FILTERS_SUBMIT,
      PAGINATE_CHANGE,
      SEARCH_CHANGE
    ),
    filter(() => matchURL(URL_CONST.groupSettings.self)),
    mergeMap(() => {
      const state = state$.value
      const selfState = state.groupsettings.self
      const selectedCompany = selectCompanyFilter(state)?.value

      return api.groupSettings
        .getGroupSettingsTableData(
          selfState.page || 1,
          selfState.sortProperty || '',
          selfState.filters?.assignedFilter || '',
          selfState.filters?.updatedByFilter || [],
          getRangeFromDateFilter(selfState.filters?.lastUpdatedFilter),
          selfState.search || '',
          selectedCompany
        )
        .pipe(
          map((response) => fetchGroupSettingsSuccess(response)),
          catchError((error) =>
            of(epicsErrorHandler(fetchGroupSettingsError, error, [400, 403, 404]))
          ),
          takeUntil(action$.pipe(ofType(GROUP_SETTINGS_DATA_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const cloneGroupSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CLONE_GROUP_SETTINGS_REQUEST_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value
      const selectedCompany = selectCompanyFilter(state)?.value

      return api.groupSettings
        .cloneGroupSettings(selectedCompany, {
          groupSettingsId: action.payload.id,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(cloneGroupSettingsSuccess(response), addToast({ message: 'Cloned successfully!' }))
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(cloneGroupSettingsError, error, [400, 403, 404]),
              addToast({
                message: error.response?.message || error?.response,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(CLONE_GROUP_SETTINGS_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const deleteGroupSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_GROUP_SETTINGS_REQUEST_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value
      const selectedCompany = selectCompanyFilter(state)?.value

      const groupSettingsId = action.payload.id || state.groupsettings?.wizard?.data?.id

      if (!groupSettingsId) {
        return EMPTY
      }

      return api.groupSettings
        .deleteGroupSettings(selectedCompany, {
          groupSettingsId,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              deleteGroupSettingsSuccess(response),
              matchURL(URL_CONST.groupSettings.table) &&
                addToast({ message: 'Deleted successfully!' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(deleteGroupSettingsError, error, [400, 403, 404]),
              matchURL(URL_CONST.groupSettings.table) &&
                addToast({
                  message: error.response?.message || error?.response,
                  type: 'error',
                })
            )
          ),
          takeUntil(action$.pipe(ofType(DELETE_GROUP_SETTINGS_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const setGroupSettingsAsDefaultEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SET_GROUP_SETTINGS_AS_DEFAULT_REQUEST_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value
      const selectedCompany = selectCompanyFilter(state)?.value

      return api.groupSettings
        .setGroupSettingsAsDefault(selectedCompany, {
          groupSettingsId: action.payload.id,
          setAsDefault: action.payload.value,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              setGroupSettingsAsDefaultSuccess(response),
              addToast({ message: 'Applied successfully!' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(setGroupSettingsAsDefaultError, error, [400, 403, 404]),
              addToast({
                message: error.response?.message || error?.response,
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(SET_GROUP_SETTINGS_AS_DEFAULT_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  cloneGroupSettingsEpic,
  deleteGroupSettingsEpic,
  fetchGroupSettingsEpic,
  setGroupSettingsAsDefaultEpic
)
